import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, ArrowRight } from "lucide-react";
import { useSilver, scrollToId } from "@/context/SilverContext";
import { CONTACT } from "@/data/content";
import { formatGBP } from "@/config/pricing";
import { fmtInt } from "@/components/silver/common/AnimatedNumber";

const EASE = [0.22, 1, 0.36, 1];

function DeliveryScene() {
  return (
    <svg viewBox="0 0 520 260" className="h-auto w-full max-w-[520px]" aria-hidden="true">
      <defs>
        <linearGradient id="se-env" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff" /><stop offset="1" stopColor="#E9EDE1" /></linearGradient>
        <radialGradient id="se-glow"><stop offset="0" stopColor="rgba(198,238,90,0.9)" /><stop offset="1" stopColor="rgba(150,193,31,0)" /></radialGradient>
      </defs>
      <path d="M20 232 H500" stroke="rgba(255,255,255,0.12)" />
      <path d="M40 120 C 140 40, 260 40, 352 176" fill="none" stroke="rgba(198,238,90,0.35)" strokeDasharray="3 7" />
      <g transform="translate(372 150)" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.6" strokeLinejoin="round">
        <path d="M-70 -20 L0 -80 L70 -20" />
        <path d="M-58 -28 V82 H58 V-28" />
        <path d="M-18 82 V30 H18 V82" />
        <rect x="-44" y="-6" width="20" height="18" />
        <rect x="24" y="-6" width="20" height="18" />
      </g>
      <motion.circle cx="372" cy="200" r="40" fill="url(#se-glow)" initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0.6] }} transition={{ delay: 1.5, duration: 1.4 }} />
      <motion.rect x="361" y="196" width="22" height="6" rx="2" initial={{ fill: "rgba(255,255,255,0.3)" }} animate={{ fill: "#C6EE5A" }} transition={{ delay: 1.45, duration: 0.4 }} />
      <motion.g initial={{ x: -10, y: 70, rotate: -12, scale: 1, opacity: 0 }}
        animate={{ x: [-10, 120, 300, 372], y: [70, 10, 40, 199], rotate: [-12, -4, 6, 0], scale: [1, 1, 0.8, 0.12], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 1.7, ease: "easeInOut", times: [0, 0.35, 0.75, 1] }}>
        <g transform="translate(0 0)">
          <rect x="-46" y="-30" width="92" height="60" rx="5" fill="url(#se-env)" />
          <path d="M-46 -26 L0 6 L46 -26" fill="none" stroke="rgba(18,41,15,0.2)" strokeWidth="1.4" />
          <circle cx="0" cy="6" r="6" fill="#96C11F" />
        </g>
      </motion.g>
      <motion.g initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.9, type: "spring", stiffness: 260, damping: 15 }} style={{ transformOrigin: "372px 52px" }}>
        <circle cx="372" cy="52" r="26" fill="#96C11F" />
        <motion.path d="M360 52 l8 8 l16 -17" fill="none" stroke="#12290F" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 2.15, duration: 0.45 }} />
      </motion.g>
    </svg>
  );
}

export function SuccessExperience() {
  const { submission, resetAll } = useSilver();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  const p = submission?.payload;
  const copy = async () => {
    try { await navigator.clipboard.writeText(submission.reference); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { setCopied(false); }
  };
  if (!p) return null;

  return (
    <motion.div role="dialog" aria-modal="true" aria-labelledby="success-title" className="fixed inset-0 z-[70] overflow-y-auto bg-[#0B1C0D]/95 backdrop-blur-xl"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} data-testid="success-experience">
      <div className="pointer-events-none absolute left-1/2 top-[30%] h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(150,193,31,0.2),transparent_60%)]" aria-hidden="true" />
      <div className="relative mx-auto flex min-h-full max-w-3xl flex-col items-center justify-center px-5 py-16 text-center">
        <DeliveryScene />
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.3, duration: 0.9, ease: EASE }}>
          <p className="eyebrow mt-6">Brief received</p>
          <h2 id="success-title" className="mt-4 text-4xl font-extrabold tracking-[-0.045em] sm:text-5xl" data-testid="success-heading">Your campaign brief is with us.</h2>
          <p className="mx-auto mt-5 max-w-xl text-[16px] leading-relaxed text-white/65">
            The NicheCom Print team will review your audience and campaign requirements and get in touch to finalise the specification.
          </p>
          <button type="button" onClick={copy} className="mt-8 inline-flex items-center gap-3 rounded-full border border-[#96C11F]/50 bg-[#96C11F]/10 px-5 py-3 font-ui text-[14px] font-semibold tracking-[0.08em] text-white" data-testid="success-reference">
            {submission.reference}
            {copied ? <Check className="h-4 w-4 text-[#C6EE5A]" /> : <Copy className="h-4 w-4 text-white/50" />}
          </button>
          <dl className="mx-auto mt-8 grid max-w-xl grid-cols-3 gap-4 border-y border-white/10 py-5 text-left">
            <div><dt className="eyebrow-muted">Areas</dt><dd className="mt-1 truncate text-[14px] font-semibold">{p.postcodeDistricts.join(" · ")}</dd></div>
            <div><dt className="eyebrow-muted">Reach</dt><dd className="num mt-1 text-[14px] font-semibold">{fmtInt(p.selectedQuantity)}</dd></div>
            <div><dt className="eyebrow-muted">Est. base</dt><dd className="num mt-1 text-[14px] font-semibold text-[#C6EE5A]">{formatGBP(submission.estimatedPrice ?? p.estimatedPrice)}</dd></div>
          </dl>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button type="button" className="btn-primary" onClick={() => { resetAll(); requestAnimationFrame(() => scrollToId("build", "auto")); }} data-testid="build-another-audience-btn">
              Build another audience <ArrowRight className="h-4 w-4" />
            </button>
            <a href={CONTACT.site} className="btn-ghost" data-testid="return-to-nichecom-link">Return to NicheCom</a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
